import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Tooltip,
    LinearProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import CloudIcon from '@mui/icons-material/Cloud';
import QueueIcon from '@mui/icons-material/Queue';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts';
import { Link as RouterLink } from 'react-router-dom';
import { getServersHealth, getReplicationHealth, getDeploymentsHealth, getQueuesHealth, getCronsHealth, sendHealthReport, purgeHealthQueue, retryHealthDeadLetterQueue, resyncDlqMatches, getAiHealth, AI_LABELS } from '../Api';
import ServerHealthStatusResponseDto, { ServerHealthMetricStatusResponse, ServerHealthStatus } from '../models/dtos/ServerHealthStatusResponseDto';
import ReplicationHealthResponseDto, { ReplicationStatus } from '../models/dtos/ReplicationHealthResponseDto';
import KubernetesDeploymentHealthResponseDto, { KubernetesDeploymentHealthStatus } from '../models/dtos/KubernetesDeploymentHealthResponseDto';
import QueueStatusInfoDto, { QueueHealthStatus, QueueCategory } from '../models/dtos/QueueStatusInfoDto';
import CronHealthResponseDto, { CronTaskHealthStatusResponse, CronTaskHealthStatus } from '../models/dtos/CronHealthResponseDto';
import { DashboardAiHealthDto } from '../models/dtos/AiDto';
import AiHealthPanels, { AiHealthOverviewTile } from '../components/ai/AiHealthPanels';
import { aiLoadErrorText, AiHealthChip } from '../components/ai/AiShared';
import PsychologyIcon from '@mui/icons-material/Psychology';

// ─── Color constants ─────────────────────────────────────────────────────────

const COLOR_GREEN = '#4caf50';
const COLOR_YELLOW = '#ffeb3b';
const COLOR_ORANGE = '#ff9800';
const COLOR_RED = '#f44336';
const COLOR_GRAY = '#9e9e9e';
const COLOR_UNUSED_GRAY = '#e0e0e0';
const QUEUE_ACTION_BUTTON_WIDTH = 136;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// ─── Status helpers ──────────────────────────────────────────────────────────

// Server: HEALTHY(green) → UNDER_PRESSURE(yellow) → SICK(orange) → DEAD(red)
const getServerStatusHex = (status: ServerHealthStatus): string => {
    switch (status) {
        case 'HEALTHY': return COLOR_GREEN;
        case 'UNDER_PRESSURE': return COLOR_YELLOW;
        case 'SICK': return COLOR_ORANGE;
        case 'DEAD': return COLOR_RED;
        default: return COLOR_GRAY;
    }
};

const getServerStatusChipColor = (status: ServerHealthStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'HEALTHY': return 'success';
        case 'UNDER_PRESSURE': return 'warning';
        case 'SICK': return 'warning';
        case 'DEAD': return 'error';
        default: return 'default';
    }
};

const getServerStatusIcon = (status: ServerHealthStatus) => {
    switch (status) {
        case 'HEALTHY': return <CheckCircleIcon sx={{ color: COLOR_GREEN }} fontSize="small" />;
        case 'UNDER_PRESSURE': return <WarningIcon sx={{ color: COLOR_YELLOW }} fontSize="small" />;
        case 'SICK': return <WarningIcon sx={{ color: COLOR_ORANGE }} fontSize="small" />;
        case 'DEAD': return <ErrorIcon sx={{ color: COLOR_RED }} fontSize="small" />;
        default: return <HelpOutlineIcon color="disabled" fontSize="small" />;
    }
};

// Replication: ACTUAL(green) → BEHIND(orange) → STOPPED(red), UNKNOWN(red)
const getReplicationStatusChipColor = (status: ReplicationStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'ACTUAL': return 'success';
        case 'BEHIND': return 'warning';
        case 'STOPPED': return 'error';
        case 'UNKNOWN': return 'error';
        default: return 'default';
    }
};

const getReplicationStatusIcon = (status: ReplicationStatus) => {
    switch (status) {
        case 'ACTUAL': return <CheckCircleIcon color="success" />;
        case 'BEHIND': return <WarningIcon sx={{ color: COLOR_ORANGE }} />;
        case 'STOPPED': return <ErrorIcon color="error" />;
        case 'UNKNOWN': return <ErrorIcon color="error" />;
        default: return <HelpOutlineIcon color="disabled" />;
    }
};

// Deployment: HEALTHY(green) → SICK(orange) → DEAD(red), STOPPED(gray)
const getDeploymentStatusHex = (status: KubernetesDeploymentHealthStatus): string => {
    switch (status) {
        case 'HEALTHY': return COLOR_GREEN;
        case 'SICK': return COLOR_ORANGE;
        case 'DEAD': return COLOR_RED;
        case 'STOPPED': return COLOR_GRAY;
        default: return COLOR_GRAY;
    }
};

const getDeploymentStatusChipColor = (status: KubernetesDeploymentHealthStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'HEALTHY': return 'success';
        case 'SICK': return 'warning';
        case 'DEAD': return 'error';
        case 'STOPPED': return 'default';
        default: return 'default';
    }
};

// Queue: HEALTHY(green) → SICK(orange) → DEAD(red)
const getQueueStatusChipColor = (status: QueueHealthStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'HEALTHY': return 'success';
        case 'SICK': return 'warning';
        case 'DEAD': return 'error';
        default: return 'default';
    }
};

const getQueueStatusHex = (status: QueueHealthStatus): string => {
    switch (status) {
        case 'HEALTHY': return COLOR_GREEN;
        case 'SICK': return COLOR_ORANGE;
        case 'DEAD': return COLOR_RED;
        default: return COLOR_GRAY;
    }
};

// Cron: HEALTHY(green) → SLOW(yellow) → SICK(orange) → DEAD(red)
const getCronStatusChipColor = (status: CronTaskHealthStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'HEALTHY': return 'success';
        case 'SLOW': return 'warning';
        case 'SICK': return 'warning';
        case 'DEAD': return 'error';
        default: return 'default';
    }
};

const getCronStatusHex = (status: CronTaskHealthStatus): string => {
    switch (status) {
        case 'HEALTHY': return COLOR_GREEN;
        case 'SLOW': return COLOR_YELLOW;
        case 'SICK': return COLOR_ORANGE;
        case 'DEAD': return COLOR_RED;
        default: return COLOR_GRAY;
    }
};

// ─── Pie chart helpers ──────────────────────────────────────────────────────

/** Parse a percentage value from a metric message like "Usage: 42%" or "72% used" */
const parseUsagePercent = (message: string): number | null => {
    const match = message.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? parseFloat(match[1]) : null;
};

/**
 * CPU / RAM / Disk: pie shows used (colored by status) vs unused (gray).
 * Falls back to full colored circle if no percentage is parseable.
 */
const buildUsagePieData = (metric: ServerHealthMetricStatusResponse) => {
    const pct = parseUsagePercent(metric.message);
    const color = getServerStatusHex(metric.status);
    if (pct !== null) {
        return [
            { id: 0, value: pct, label: `Used ${pct}%`, color },
            { id: 1, value: 100 - pct, label: `Free ${(100 - pct).toFixed(1)}%`, color: COLOR_UNUSED_GRAY },
        ];
    }
    // No parseable percentage — show single slice representing overall status
    return [{ id: 0, value: 1, label: metric.status, color }];
};

/**
 * S.M.A.R.T.: pie shows healthy disks (green) vs problematic disks (colored by their status).
 */
const buildSmartPieData = (metric: ServerHealthMetricStatusResponse) => {
    if (metric.details.length === 0) {
        return [{ id: 0, value: 1, label: metric.status, color: getServerStatusHex(metric.status) }];
    }
    return metric.details.map((d, idx) => ({
        id: idx,
        value: 1,
        label: `${d.item} (${d.status})`,
        color: d.status === 'HEALTHY' ? COLOR_GREEN : getServerStatusHex(d.status),
    }));
};

// ─── Server Card Component ──────────────────────────────────────────────────

interface ServerCardProps {
    server: ServerHealthStatusResponseDto;
}

const MetricSection: React.FC<{ label: string; metric: ServerHealthMetricStatusResponse; expandable: boolean }> = ({ label, metric, expandable }) => {
    const [expanded, setExpanded] = useState(false);
    const hasDetails = metric.details.length > 0;

    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getServerStatusIcon(metric.status)}
                    <Typography variant="body2" fontWeight="bold">{label}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Arrow before badge for expandable metrics (Disk Space & S.M.A.R.T.) */}
                    {expandable && hasDetails ? (
                        <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.25 }}>
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    ) : expandable ? (
                        /* Invisible placeholder so badge stays aligned */
                        <Box sx={{ width: 24, height: 24 }} />
                    ) : null}
                    <Chip label={metric.status} color={getServerStatusChipColor(metric.status)} size="small" variant="outlined" />
                </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>{metric.message}</Typography>
            {hasDetails && expandable && (
                <Collapse in={expanded}>
                    <Box sx={{ ml: 3.5, mt: 0.5 }}>
                        {metric.details.map((detail, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                                {getServerStatusIcon(detail.status)}
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>{detail.item}:</Typography>
                                <Typography variant="caption" color="text.secondary">{detail.message}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

const ServerCard: React.FC<ServerCardProps> = ({ server }) => {
    const pieMetrics = [
        { label: 'CPU', data: buildUsagePieData(server.cpu) },
        { label: 'RAM', data: buildUsagePieData(server.ram) },
        { label: 'Disk', data: buildUsagePieData(server.space) },
        { label: 'S.M.A.R.T.', data: buildSmartPieData(server.smart) },
    ];

    // Expandable metrics: Disk Space & S.M.A.R.T. have meaningful detail items
    const metricsList: { label: string; metric: ServerHealthMetricStatusResponse; expandable: boolean }[] = [
        { label: 'CPU', metric: server.cpu, expandable: false },
        { label: 'RAM', metric: server.ram, expandable: false },
        { label: 'Disk Space', metric: server.space, expandable: true },
        { label: 'S.M.A.R.T.', metric: server.smart, expandable: true },
    ];

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box
                        component={RouterLink}
                        to={`/health/servers/${encodeURIComponent(server.serverId)}`}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit', '&:hover': { color: 'primary.main' } }}
                    >
                        <DnsIcon color="primary" />
                        <Typography variant="h6">{server.serverId}</Typography>
                    </Box>
                    <Chip label={server.status} color={getServerStatusChipColor(server.status)} size="small" />
                </Box>

                {server.lastReportedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Last reported: {new Date(server.lastReportedAt).toLocaleString()}
                    </Typography>
                )}

                {/* Pie charts for metrics overview */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    {pieMetrics.map((pm, idx) => (
                        <Grid item xs={6} key={idx}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" fontWeight="bold">{pm.label}</Typography>
                                <PieChart
                                    series={[{
                                        data: pm.data,
                                        innerRadius: 18,
                                        outerRadius: 45,
                                        paddingAngle: 1,
                                        cornerRadius: 3,
                                    }]}
                                    height={120}
                                    width={160}
                                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Detailed metrics list */}
                {metricsList.map((m, idx) => (
                    <MetricSection key={idx} label={m.label} metric={m.metric} expandable={m.expandable} />
                ))}
            </CardContent>
        </Card>
    );
};

// ─── Main Health Page ────────────────────────────────────────────────────────

const HealthPage: React.FC = () => {
    const navigate = useNavigate();
    const [servers, setServers] = useState<ServerHealthStatusResponseDto[]>([]);
    const [replication, setReplication] = useState<ReplicationHealthResponseDto | null>(null);
    const [deployments, setDeployments] = useState<KubernetesDeploymentHealthResponseDto[]>([]);
    const [queues, setQueues] = useState<QueueStatusInfoDto[]>([]);
    const [crons, setCrons] = useState<CronHealthResponseDto | null>(null);
    const [aiHealth, setAiHealth] = useState<DashboardAiHealthDto | null>(null);
    const [aiHealthError, setAiHealthError] = useState<string | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Report modal state
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportEmail, setReportEmail] = useState('');
    const [reportEmailError, setReportEmailError] = useState<string | null>(null);
    const [reportSending, setReportSending] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'warning' | 'error' }>({ open: false, message: '', severity: 'success' });
    const [queueActionDialog, setQueueActionDialog] = useState<{
        open: boolean;
        action: 'purge' | 'retry' | 'resync';
        queue: QueueStatusInfoDto | null;
    }>({ open: false, action: 'purge', queue: null });
    const [queueActionLoading, setQueueActionLoading] = useState(false);
    const [queueRefetching, setQueueRefetching] = useState(false);

    const handleOpenReportModal = () => {
        setReportEmail('');
        setReportEmailError(null);
        setReportModalOpen(true);
    };

    const handleCloseReportModal = () => {
        setReportModalOpen(false);
        setReportEmail('');
        setReportEmailError(null);
    };

    const validateEmail = (email: string): boolean => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSendReport = async () => {
        if (!reportEmail.trim()) {
            setReportEmailError('Email is required');
            return;
        }
        if (!validateEmail(reportEmail.trim())) {
            setReportEmailError('Please provide a valid email address');
            return;
        }
        setReportEmailError(null);
        setReportSending(true);
        try {
            const success = await sendHealthReport(reportEmail.trim());
            if (success) {
                setSnackbar({ open: true, message: 'Health report sent successfully!', severity: 'success' });
                handleCloseReportModal();
            } else {
                setSnackbar({ open: true, message: 'Failed to send health report.', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to send health report.', severity: 'error' });
        } finally {
            setReportSending(false);
        }
    };

    const refreshQueues = useCallback(async () => {
        const queuesData = await getQueuesHealth();
        setQueues(queuesData);
    }, []);

    const openQueueActionDialog = (action: 'purge' | 'retry' | 'resync', queue: QueueStatusInfoDto) => {
        setQueueActionDialog({ open: true, action, queue });
    };

    const closeQueueActionDialog = () => {
        if (!queueActionLoading) {
            setQueueActionDialog({ open: false, action: 'purge', queue: null });
        }
    };

    const refetchQueuesAfterDelay = useCallback(async () => {
        setQueueRefetching(true);
        try {
            await wait(5000);
            await refreshQueues();
        } finally {
            setQueueRefetching(false);
        }
    }, [refreshQueues]);

    const handleConfirmQueueAction = async () => {
        if (!queueActionDialog.queue) return;

        const { action, queue } = queueActionDialog;
        setQueueActionLoading(true);
        try {
            if (action === 'resync') {
                const result = await resyncDlqMatches('Xbet');
                if (result === null) {
                    throw new Error('Failed to re-sync DLQ matches');
                }
                setSnackbar({
                    open: true,
                    message: result === false
                        ? 'DLQ matches queue is already empty.'
                        : `Re-sync requested for failed match messages from ${queue.queueName}.`,
                    severity: result === false ? 'warning' : 'success'
                });
            } else {
                const result = action === 'purge'
                    ? await purgeHealthQueue(queue.queueName)
                    : await retryHealthDeadLetterQueue(queue.queueName);
                const isProviderSyncRetry = action === 'retry' && queue.category === 'ProviderSync';
                const message = action === 'purge'
                    ? `Purged ${result.affectedMessages.toLocaleString()} messages from ${result.queueName}.`
                    : isProviderSyncRetry
                        ? `Retried ${result.affectedMessages.toLocaleString()} messages from ${result.queueName} to ${result.movedToQueueName}. For syncing match entities, it is better to use the sync mechanism from the Matches page. Old match information from failed messages may override more recent data in DB.`
                        : `Retried ${result.affectedMessages.toLocaleString()} messages from ${result.queueName} to ${result.movedToQueueName}.`;
                setSnackbar({ open: true, message, severity: isProviderSyncRetry ? 'warning' : 'success' });
            }
            setQueueActionDialog({ open: false, action: 'purge', queue: null });
            await refetchQueuesAfterDelay();
        } catch (err) {
            console.error('Error managing queue:', err);
            setSnackbar({ open: true, message: 'Failed to update queue.', severity: 'error' });
        } finally {
            setQueueActionLoading(false);
        }
    };

    // AI health is loaded on its own (outside the Promise.all) so a failing AI service never breaks the page.
    const loadAiHealth = useCallback(async () => {
        try {
            const data = await getAiHealth();
            setAiHealth(data);
            setAiHealthError(data == null ? aiLoadErrorText(AI_LABELS.health, 'Failed to load AI health.') : null);
        } catch (err) {
            console.error('Error loading AI health:', err);
            setAiHealth(null);
            setAiHealthError('Failed to load AI health.');
        }
    }, []);

    const loadData = useCallback(async () => {
        loadAiHealth();
        try {
            setLoading(true);
            setError(null);
            const [serversData, replicationData, deploymentsData, queuesData, cronsData] = await Promise.all([
                getServersHealth(),
                getReplicationHealth(),
                getDeploymentsHealth(),
                getQueuesHealth(),
                getCronsHealth(),
            ]);
            setServers(serversData);
            setReplication(replicationData);
            setDeployments(deploymentsData);
            setQueues(queuesData);
            setCrons(cronsData);
        } catch (err) {
            console.error('Error loading health data:', err);
            setError('Failed to load health data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [loadAiHealth]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ── Deployment summary for pie chart
    const deploymentStatusCounts = deployments.reduce((acc, d) => {
        acc[d.status] = (acc[d.status] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const deploymentPieData = Object.entries(deploymentStatusCounts).map(([status, count], idx) => ({
        id: idx,
        value: count,
        label: status,
        color: getDeploymentStatusHex(status as KubernetesDeploymentHealthStatus)
    }));

    // ── Queue categories
    const queueCategories = Array.from(new Set(queues.map(q => q.category)));
    const selectedQueue = queueActionDialog.queue;
    const selectedQueueActionLabel = queueActionDialog.action === 'purge'
        ? 'Cleanup Queue'
        : queueActionDialog.action === 'resync'
            ? 'Re-sync DLQ Matches'
            : 'Retry DLQ';
    const selectedQueueProviderSyncRetry = queueActionDialog.action === 'retry' && selectedQueue?.category === 'ProviderSync';

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">
                    System Health Dashboard
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<TimelineIcon />} onClick={() => navigate('/health/provider-tasks')}>
                        Provider task history
                    </Button>
                    <Button variant="outlined" startIcon={<EmailIcon />} onClick={handleOpenReportModal}>
                        Generate Report
                    </Button>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {/* ═══════════════ OVERVIEW ═══════════════ */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Overview</Typography>
                <Grid container spacing={2}>
                    {/* ── Servers overview card ── */}
                    <Grid item xs={12} md={6} lg={3}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <StorageIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight="bold">Servers</Typography>
                                    </Box>
                                    <Chip label={`${servers.length}`} size="small" variant="outlined" />
                                </Box>
                                {servers.length > 0 && (() => {
                                    const serverStatusCounts = servers.reduce((acc, s) => {
                                        acc[s.status] = (acc[s.status] || 0) + 1;
                                        return acc;
                                    }, {} as Record<string, number>);
                                    const serverPieData = Object.entries(serverStatusCounts).map(([status, count], idx) => ({
                                        id: idx,
                                        value: count,
                                        label: `${status} (${count})`,
                                        color: getServerStatusHex(status as ServerHealthStatus),
                                    }));
                                    return (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                            <PieChart
                                                series={[{
                                                    data: serverPieData,
                                                    innerRadius: 20,
                                                    outerRadius: 50,
                                                    paddingAngle: 2,
                                                    cornerRadius: 3,
                                                }]}
                                                height={120}
                                                width={200}
                                                margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                                slotProps={{ legend: { hidden: true } }}
                                            />
                                        </Box>
                                    );
                                })()}
                                {servers.map((s, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {getServerStatusIcon(s.status)}
                                            <Typography variant="body2">{s.serverId}</Typography>
                                        </Box>
                                        <Chip label={s.status} color={getServerStatusChipColor(s.status)} size="small" variant="outlined" />
                                    </Box>
                                ))}
                                <Box
                                    component="a"
                                    href="#section-servers"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                >
                                    <Typography variant="caption" fontWeight="bold">View details</Typography>
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── Replication overview card ── */}
                    <Grid item xs={12} md={6} lg={3}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DnsIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight="bold">Replication</Typography>
                                </Box>
                                {replication ? (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Channel</Typography>
                                            <Typography variant="body2" fontWeight="bold">{replication.channelName}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Status</Typography>
                                            <Chip
                                                icon={getReplicationStatusIcon(replication.status)}
                                                label={replication.status}
                                                color={getReplicationStatusChipColor(replication.status)}
                                                size="small"
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">Lag</Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {replication.secondsBehindMaster !== null ? `${replication.secondsBehindMaster}s` : 'N/A'}
                                            </Typography>
                                        </Box>
                                        {replication.secondsBehindMaster !== null && (
                                            <Tooltip title={`${replication.secondsBehindMaster} seconds behind master`}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min((replication.secondsBehindMaster / 60) * 100, 100)}
                                                    color={replication.secondsBehindMaster < 5 ? 'success' : replication.secondsBehindMaster < 30 ? 'warning' : 'error'}
                                                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                                                />
                                            </Tooltip>
                                        )}
                                        {replication.msg && (
                                            <Typography variant="caption" color="text.secondary">{replication.msg}</Typography>
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No data</Typography>
                                )}
                                <Box
                                    component="a"
                                    href="#section-replication"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                >
                                    <Typography variant="caption" fontWeight="bold">View details</Typography>
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── Deployments overview card ── */}
                    <Grid item xs={12} md={6} lg={3}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CloudIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight="bold">Deployments</Typography>
                                    </Box>
                                    <Chip label={`${deployments.length}`} size="small" variant="outlined" />
                                </Box>
                                {deployments.length > 0 && (() => {
                                    const depPie = Object.entries(
                                        deployments.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {} as Record<string, number>)
                                    ).map(([status, count], idx) => ({
                                        id: idx,
                                        value: count,
                                        label: `${status} (${count})`,
                                        color: getDeploymentStatusHex(status as KubernetesDeploymentHealthStatus),
                                    }));
                                    const totalRequired = deployments.reduce((s, d) => s + d.requiredReplicas, 0);
                                    const totalReady = deployments.reduce((s, d) => s + d.readyReplicas, 0);
                                    const totalUnavailable = deployments.reduce((s, d) => s + d.unavailableReplicas, 0);
                                    const namespaces = Array.from(new Set(deployments.map(d => d.namespace)));
                                    return (
                                        <>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                                <PieChart
                                                    series={[{
                                                        data: depPie,
                                                        innerRadius: 20,
                                                        outerRadius: 50,
                                                        paddingAngle: 2,
                                                        cornerRadius: 3,
                                                    }]}
                                                    height={120}
                                                    width={200}
                                                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                                    slotProps={{ legend: { hidden: true } }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">Required</Typography>
                                                <Typography variant="body2" fontWeight="bold">{totalRequired}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">Ready</Typography>
                                                <Typography variant="body2" fontWeight="bold" color={totalReady < totalRequired ? 'error.main' : 'success.main'}>{totalReady}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">Unavailable</Typography>
                                                <Typography variant="body2" fontWeight="bold" color={totalUnavailable > 0 ? 'error.main' : 'text.primary'}>{totalUnavailable}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                {namespaces.map((ns) => (
                                                    <Chip key={ns} label={ns} size="small" variant="outlined" />
                                                ))}
                                            </Box>
                                        </>
                                    );
                                })()}
                                <Box
                                    component="a"
                                    href="#section-deployments"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                >
                                    <Typography variant="caption" fontWeight="bold">View details</Typography>
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── Queues overview card ── */}
                    <Grid item xs={12} md={6} lg={3}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <QueueIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight="bold">Queues</Typography>
                                    </Box>
                                    <Chip label={`${queues.length}`} size="small" variant="outlined" />
                                </Box>
                                {queues.length > 0 && (() => {
                                    const categories: QueueCategory[] = ['ProviderSync', 'UserSync', 'BetCalculation'];
                                    const CATEGORY_LABELS: Record<QueueCategory, string> = {
                                        'ProviderSync': 'Provider Sync',
                                        'UserSync': 'User Sync',
                                        'BetCalculation': 'Bet Calculation',
                                    };
                                    return (
                                        <>
                                            {categories.map((cat) => {
                                                const catQueues = queues.filter(q => q.category === cat);
                                                if (catQueues.length === 0) return null;
                                                const statusCounts = catQueues.reduce((acc, q) => {
                                                    acc[q.status] = (acc[q.status] || 0) + 1;
                                                    return acc;
                                                }, {} as Record<string, number>);
                                                const catPieData = Object.entries(statusCounts).map(([status, count], idx) => ({
                                                    id: idx,
                                                    value: count,
                                                    label: `${status} (${count})`,
                                                    color: getQueueStatusHex(status as QueueHealthStatus),
                                                }));
                                                return (
                                                    <Box key={cat} sx={{ mb: 1 }}>
                                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                            {CATEGORY_LABELS[cat]} ({catQueues.length})
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <PieChart
                                                                series={[{
                                                                    data: catPieData,
                                                                    innerRadius: 14,
                                                                    outerRadius: 35,
                                                                    paddingAngle: 2,
                                                                    cornerRadius: 3,
                                                                }]}
                                                                height={90}
                                                                width={170}
                                                                margin={{ top: 2, bottom: 2, left: 2, right: 2 }}
                                                                slotProps={{ legend: { hidden: true } }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </>
                                    );
                                })()}
                                <Box
                                    component="a"
                                    href="#section-queues"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                >
                                    <Typography variant="caption" fontWeight="bold">View details</Typography>
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── Crons overview card ── */}
                    <Grid item xs={12} md={6} lg={3}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ScheduleIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight="bold">Cron Tasks</Typography>
                                    </Box>
                                    <Chip label={`${crons?.tasks.length ?? 0}`} size="small" variant="outlined" />
                                </Box>
                                {crons && !crons.statusAvailable && (
                                    <Alert severity="warning" sx={{ mb: 1 }}>{crons.errorMessage || 'Status unavailable'}</Alert>
                                )}
                                {crons && crons.tasks.length > 0 && (() => {
                                    const cronStatusCounts = crons.tasks.reduce((acc, t) => {
                                        acc[t.status] = (acc[t.status] || 0) + 1;
                                        return acc;
                                    }, {} as Record<string, number>);
                                    const cronPieData = Object.entries(cronStatusCounts).map(([status, count], idx) => ({
                                        id: idx,
                                        value: count,
                                        label: `${status} (${count})`,
                                        color: getCronStatusHex(status as CronTaskHealthStatus),
                                    }));
                                    return (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                            <PieChart
                                                series={[{
                                                    data: cronPieData,
                                                    innerRadius: 20,
                                                    outerRadius: 50,
                                                    paddingAngle: 2,
                                                    cornerRadius: 3,
                                                }]}
                                                height={120}
                                                width={200}
                                                margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                                                slotProps={{ legend: { hidden: true } }}
                                            />
                                        </Box>
                                    );
                                })()}
                                {crons && (() => {
                                    const mostRecentExecution = crons.tasks
                                        .map(t => t.lastTimeExecutedAt)
                                        .filter((d): d is string => d !== null)
                                        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;
                                    return (
                                        <>
                                            <Box sx={{ mb: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Total Tasks</Typography>
                                                    <Typography variant="body2" fontWeight="bold">{crons.tasks.length}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">App Started</Typography>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {crons.applicationStartedAt ? new Date(crons.applicationStartedAt).toLocaleString() : 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">Latest Run</Typography>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {mostRecentExecution ? new Date(mostRecentExecution).toLocaleString() : 'Never'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                <Chip
                                                    label={crons.scheduledTasksEnabled ? 'Tasks Enabled' : 'Tasks Disabled'}
                                                    size="small"
                                                    color={crons.scheduledTasksEnabled ? 'success' : 'error'}
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={crons.rabbitmqListeningEnabled ? 'RabbitMQ On' : 'RabbitMQ Off'}
                                                    size="small"
                                                    color={crons.rabbitmqListeningEnabled ? 'success' : 'error'}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </>
                                    );
                                })()}
                                <Box
                                    component="a"
                                    href="#section-crons"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                >
                                    <Typography variant="caption" fontWeight="bold">View details</Typography>
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* ── AI overview card ── */}
                    <Grid item xs={12} md={6} lg={3}>
                        <AiHealthOverviewTile health={aiHealth} loadError={aiHealthError} />
                    </Grid>
                </Grid>
            </Box>

            {/* ═══════════════ SECTION 1: Servers ═══════════════ */}
            <Box id="section-servers" sx={{ mb: 4, scrollMarginTop: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <StorageIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">Servers</Typography>
                    <Chip label={`${servers.length} servers`} size="small" variant="outlined" />
                </Box>
                {servers.length === 0 ? (
                    <Alert severity="info">No server data available.</Alert>
                ) : (
                    <Grid container spacing={3}>
                        {servers.map((server, idx) => (
                            <Grid item xs={12} md={6} lg={4} key={idx}>
                                <ServerCard server={server} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* ═══════════════ SECTION 2: Replication ═══════════════ */}
            <Box id="section-replication" sx={{ mb: 4, scrollMarginTop: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DnsIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">Database Replication</Typography>
                </Box>
                {replication ? (
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Channel</Typography>
                                    <Typography variant="h6">{replication.channelName}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip
                                        icon={getReplicationStatusIcon(replication.status)}
                                        label={replication.status}
                                        color={getReplicationStatusChipColor(replication.status)}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Seconds Behind Master</Typography>
                                    <Typography variant="h6">
                                        {replication.secondsBehindMaster !== null ? replication.secondsBehindMaster : 'N/A'}
                                    </Typography>
                                </Box>
                                {replication.msg && (
                                    <Box sx={{ flexBasis: '100%' }}>
                                        <Typography variant="subtitle2" color="text.secondary">Message</Typography>
                                        <Typography variant="body2">{replication.msg}</Typography>
                                    </Box>
                                )}
                            </Box>
                            {/* Lag indicator bar */}
                            {replication.secondsBehindMaster !== null && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Replication Lag</Typography>
                                    <Tooltip title={`${replication.secondsBehindMaster} seconds behind master`}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min((replication.secondsBehindMaster / 60) * 100, 100)}
                                            color={replication.secondsBehindMaster < 5 ? 'success' : replication.secondsBehindMaster < 30 ? 'warning' : 'error'}
                                            sx={{ height: 10, borderRadius: 5, mt: 0.5 }}
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Alert severity="info">No replication data available.</Alert>
                )}
            </Box>

            {/* ═══════════════ SECTION 3: Deployments ═══════════════ */}
            <Box id="section-deployments" sx={{ mb: 4, scrollMarginTop: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CloudIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">Kubernetes Deployments</Typography>
                    <Chip label={`${deployments.length} deployments`} size="small" variant="outlined" />
                </Box>

                {deployments.length === 0 ? (
                    <Alert severity="info">No deployment data available.</Alert>
                ) : (
                    <>
                        {/* Summary pie chart */}
                        {deploymentPieData.length > 0 && (
                            <Card variant="outlined" sx={{ mb: 2 }}>
                                <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <PieChart
                                        series={[{
                                            data: deploymentPieData,
                                            highlightScope: { faded: 'global', highlighted: 'item' },
                                            innerRadius: 40,
                                            paddingAngle: 2,
                                            cornerRadius: 4,
                                        }]}
                                        height={200}
                                        width={450}
                                    />
                                </CardContent>
                            </Card>
                        )}

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                        <TableCell><strong>Deployment</strong></TableCell>
                                        <TableCell><strong>Namespace</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell align="center"><strong>Required</strong></TableCell>
                                        <TableCell align="center"><strong>Ready</strong></TableCell>
                                        <TableCell align="center"><strong>Available</strong></TableCell>
                                        <TableCell align="center"><strong>Updated</strong></TableCell>
                                        <TableCell align="center"><strong>Unavailable</strong></TableCell>
                                        <TableCell><strong>Message</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {deployments.map((dep, idx) => (
                                        <TableRow key={idx} sx={{
                                            '&:hover': { backgroundColor: 'grey.50' },
                                            backgroundColor: dep.status === 'DEAD' ? '#ffebee' : dep.status === 'SICK' ? '#fff3e0' : 'inherit'
                                        }}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">{dep.deploymentName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={dep.namespace} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={dep.status} color={getDeploymentStatusChipColor(dep.status)} size="small" />
                                            </TableCell>
                                            <TableCell align="center">{dep.requiredReplicas}</TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color={dep.readyReplicas < dep.requiredReplicas ? 'error.main' : 'success.main'}
                                                    fontWeight="bold"
                                                >
                                                    {dep.readyReplicas}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">{dep.availableReplicas}</TableCell>
                                            <TableCell align="center">{dep.updatedReplicas}</TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color={dep.unavailableReplicas > 0 ? 'error.main' : 'text.primary'}
                                                    fontWeight={dep.unavailableReplicas > 0 ? 'bold' : 'normal'}
                                                >
                                                    {dep.unavailableReplicas}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {dep.message}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Box>

            {/* ═══════════════ SECTION 4: Queues ═══════════════ */}
            <Box id="section-queues" sx={{ mb: 4, scrollMarginTop: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <QueueIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">Message Queues</Typography>
                    <Chip label={`${queues.length} queues`} size="small" variant="outlined" />
                </Box>

                {queues.length === 0 ? (
                    <Alert severity="info">No queue data available.</Alert>
                ) : (
                    <>
                        {queueCategories.map((category) => {
                            const categoryQueues = queues.filter(q => q.category === category);
                            return (
                                <Box key={category} sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                                        {category}
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                                    <TableCell><strong>Queue Name</strong></TableCell>
                                                    <TableCell><strong>Status</strong></TableCell>
                                                    <TableCell align="right"><strong>Pending Messages</strong></TableCell>
                                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {categoryQueues.map((queue, idx) => {
                                                    const isDeadLetterQueue = queue.isDeadLetterQueue ?? queue.queueName.startsWith('dlq-');
                                                    const isMatchesDeadLetterQueue = isDeadLetterQueue && queue.queueName.startsWith('dlq-matches');
                                                    const queueRetryAction = isMatchesDeadLetterQueue ? 'resync' : 'retry';
                                                    const retryEnabled = isDeadLetterQueue && (isMatchesDeadLetterQueue || queue.canRetry);
                                                    const retryLabel = isMatchesDeadLetterQueue ? 'Re-sync' : 'Retry';
                                                    const retryTooltip = !isDeadLetterQueue
                                                        ? 'Retry is available only for dead letter queues.'
                                                        : isMatchesDeadLetterQueue
                                                            ? 'Re-sync failed match messages with provider.'
                                                            : retryEnabled
                                                                ? `Retry to ${queue.retryQueueName}`
                                                                : 'Retry unavailable because the matching origin queue does not exist.';
                                                    const actionInProgress = queueActionLoading && queueActionDialog.queue?.queueName === queue.queueName;

                                                    return (
                                                        <TableRow key={idx} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography variant="body2">{queue.queueName}</Typography>
                                                                    {isDeadLetterQueue && (
                                                                        <Chip label="DLQ" color="error" size="small" variant="outlined" />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={queue.status} color={getQueueStatusChipColor(queue.status)} size="small" />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={queue.pendingMessages > 0 ? 'bold' : 'normal'}
                                                                    color={queue.status === 'DEAD' ? 'error.main' : queue.status === 'SICK' ? 'warning.main' : 'text.primary'}
                                                                >
                                                                    {queue.pendingMessages.toLocaleString()}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Tooltip
                                                                        title={`Cleanup ${queue.queueName}`}
                                                                    >
                                                                        <span>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color="error"
                                                                                sx={{ width: QUEUE_ACTION_BUTTON_WIDTH, justifyContent: 'flex-start' }}
                                                                                startIcon={actionInProgress ? <CircularProgress size={14} /> : <DeleteSweepIcon />}
                                                                                disabled={queueActionLoading || queueRefetching}
                                                                                onClick={() => openQueueActionDialog('purge', queue)}
                                                                            >
                                                                                Cleanup
                                                                            </Button>
                                                                        </span>
                                                                    </Tooltip>
                                                                    <Tooltip title={retryTooltip}>
                                                                        <span>
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color={isMatchesDeadLetterQueue ? 'success' : 'primary'}
                                                                                sx={{ width: QUEUE_ACTION_BUTTON_WIDTH, justifyContent: 'flex-start' }}
                                                                                startIcon={actionInProgress ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                                                                                disabled={queueActionLoading || queueRefetching || !retryEnabled}
                                                                                onClick={() => openQueueActionDialog(queueRetryAction, queue)}
                                                                            >
                                                                                {retryLabel}
                                                                            </Button>
                                                                        </span>
                                                                    </Tooltip>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            );
                        })}

                        {/* Queues pending messages bar chart */}
                        {queues.some(q => q.pendingMessages > 0) && (
                            <Card variant="outlined" sx={{ mt: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Pending Messages Overview</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <BarChart
                                            xAxis={[{ scaleType: 'band', data: queues.filter(q => q.pendingMessages > 0).map(q => q.queueName) }]}
                                            series={[{
                                                data: queues.filter(q => q.pendingMessages > 0).map(q => q.pendingMessages),
                                                color: '#0989e3',
                                            }]}
                                            height={300}
                                            width={Math.max(500, queues.filter(q => q.pendingMessages > 0).length * 120)}
                                            margin={{ bottom: 100 }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    </>
                )}
            </Box>

            {/* ═══════════════ SECTION 5: Scheduled Cron Tasks ═══════════════ */}
            <Box id="section-crons" sx={{ mb: 4, scrollMarginTop: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">Scheduled Cron Tasks</Typography>
                    {crons && <Chip label={`${crons.tasks.length} tasks`} size="small" variant="outlined" />}
                </Box>

                {!crons ? (
                    <Alert severity="info">No cron task data available.</Alert>
                ) : !crons.statusAvailable ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>{crons.errorMessage || 'Cron task status is currently unavailable.'}</Alert>
                ) : (
                    <>
                        {/* Summary info */}
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Application Started</Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {crons.applicationStartedAt ? new Date(crons.applicationStartedAt).toLocaleString() : 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Scheduled Tasks</Typography>
                                        <Chip
                                            label={crons.scheduledTasksEnabled ? 'Enabled' : 'Disabled'}
                                            color={crons.scheduledTasksEnabled ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">RabbitMQ Listening</Typography>
                                        <Chip
                                            label={crons.rabbitmqListeningEnabled ? 'Enabled' : 'Disabled'}
                                            color={crons.rabbitmqListeningEnabled ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {crons.tasks.length === 0 ? (
                            <Alert severity="info">No scheduled tasks found.</Alert>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                            <TableCell><strong>Task Name</strong></TableCell>
                                            <TableCell><strong>Last Execution</strong></TableCell>
                                            <TableCell align="center"><strong>Affected Entities</strong></TableCell>
                                            <TableCell align="center"><strong>Interval (s)</strong></TableCell>
                                            <TableCell><strong>Status</strong></TableCell>
                                            <TableCell><strong>Message</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {crons.tasks.map((task, idx) => (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    '&:hover': { backgroundColor: 'grey.50' },
                                                    backgroundColor: task.status === 'DEAD' ? '#ffebee'
                                                        : task.status === 'SICK' ? '#fff3e0'
                                                        : task.status === 'SLOW' ? '#fffde7'
                                                        : 'inherit'
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">{task.taskName}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {task.lastTimeExecutedAt ? new Date(task.lastTimeExecutedAt).toLocaleString() : 'Never'}
                                                    </Typography>
                                                    {task.executionTime !== null && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Duration: {task.executionTime.toFixed(2)}s
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">
                                                        {task.lastExecutionEntitiesAffectedCount !== null ? task.lastExecutionEntitiesAffectedCount.toLocaleString() : '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">
                                                        {(task.taskInterval / 1000).toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={task.status} color={getCronStatusChipColor(task.status)} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {task.message}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}
            </Box>

            {/* ═══════════════ SECTION 6: AI ═══════════════ */}
            <Box id="section-ai" sx={{ mb: 4, scrollMarginTop: '80px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PsychologyIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">AI</Typography>
                    {aiHealth?.statusAvailable && <AiHealthChip status={aiHealth.status} />}
                    <Box component={RouterLink} to="/ai" sx={{ ml: 1, color: 'primary.main', fontSize: 14 }}>Open AI overview</Box>
                </Box>
                <AiHealthPanels health={aiHealth} loadError={aiHealthError} />
            </Box>

            {/* ═══════════════ Queue Action Modal ═══════════════ */}
            <Dialog open={queueActionDialog.open} onClose={closeQueueActionDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedQueueActionLabel}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {queueActionDialog.action === 'purge'
                            ? `Are you sure you want to cleanup the queue '${selectedQueue?.queueName}'?`
                            : queueActionDialog.action === 'resync'
                                ? `Are you sure you want to re-sync the queue '${selectedQueue?.queueName}'? It will re-sync data with provider for failed messages.`
                                : `Are you sure you want to retry the queue '${selectedQueue?.queueName}'? Messages will be pushed to '${selectedQueue?.retryQueueName}'.`}
                    </Typography>
                    {selectedQueueProviderSyncRetry && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            For syncing match entities, it is better to use the sync mechanism from the Matches page. If you proceed, old match information from failed messages may override more recent data in DB.
                        </Alert>
                    )}
                    <Typography variant="body2" fontWeight="bold">
                        {selectedQueue?.queueName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Current pending messages: {selectedQueue?.pendingMessages.toLocaleString() ?? 0}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeQueueActionDialog} disabled={queueActionLoading}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={queueActionDialog.action === 'purge' ? 'error' : 'primary'}
                        onClick={handleConfirmQueueAction}
                        disabled={queueActionLoading || queueRefetching}
                        startIcon={queueActionLoading ? <CircularProgress size={16} color="inherit" /> : queueActionDialog.action === 'purge' ? <DeleteSweepIcon /> : <ReplayIcon />}
                    >
                        {queueActionLoading ? 'Working...' : selectedQueueActionLabel}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══════════════ Queue Refetch Modal ═══════════════ */}
            <Dialog open={queueRefetching}>
                <DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body1">Re-fetching queues state</Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* ═══════════════ Report Modal ═══════════════ */}
            <Dialog open={reportModalOpen} onClose={handleCloseReportModal} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Health Report</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter the email address where you'd like to receive the system health report.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Email address"
                        type="email"
                        variant="outlined"
                        value={reportEmail}
                        onChange={(e) => {
                            setReportEmail(e.target.value);
                            if (reportEmailError) setReportEmailError(null);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendReport(); }}
                        error={!!reportEmailError}
                        helperText={reportEmailError}
                        disabled={reportSending}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseReportModal} disabled={reportSending}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSendReport}
                        disabled={reportSending}
                        startIcon={reportSending ? <CircularProgress size={16} /> : <EmailIcon />}
                    >
                        {reportSending ? 'Sending...' : 'Send Report'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══════════════ Snackbar ═══════════════ */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default HealthPage;
